import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "vaul";
import {
  CalendarDays,
  Contact,
  FolderOpen,
  Link2,
  Landmark,
  Mic2,
  PanelLeft,
  Plus,
  Ticket,
  Users,
  Wallet,
  X,
  type LucideIcon,
} from "lucide-react";

import brandLogo from "../../logos/zoniq.png";
import { CommandPalette, useCommandPaletteActions, useCommandPaletteHotkey } from "../CommandPalette";
import { GlobalSearch } from "../GlobalSearch";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { useDeviceTier } from "../../hooks/useDeviceTier";
import { useScrollToTopOnNavigate } from "../../hooks/useScrollToTopOnNavigate";
import { appNav } from "../../routes/appNavigation";
import { avatarInitials } from "../../utils/avatarInitials";
import { isOperator, isOwner, isPromoter } from "../../utils/roles";
import { MobileTabBar } from "./MobileTabBar";
import { mobileHeaderFor } from "./mobileHeader";
import "./appShell.css";

const sidebarLinkClass = ({ isActive }: { isActive: boolean }): string =>
  `app-shell-sidebar-link${isActive ? " active" : ""}`;

const DIRECTORY_LINKS: { key: string; to: string; label: string; Icon: LucideIcon }[] = [
  { key: "artists", to: appNav.artists(), label: "Artists", Icon: Mic2 },
  { key: "venues", to: appNav.venues(), label: "Venues", Icon: Landmark },
  { key: "contacts", to: appNav.contacts(), label: "Contacts", Icon: Contact },
];

const GUEST_LIST_LINK: { key: string; to: string; label: string; Icon: LucideIcon } = {
  key: "guests",
  to: appNav.guests(),
  label: "Guest List",
  Icon: Users,
};

const directoryActive = (pathname: string): boolean =>
  DIRECTORY_LINKS.some((link) => pathname.startsWith(link.to));

type NavItem = {
  key: string;
  to: string;
  label: string;
  Icon: LucideIcon;
  end?: boolean;
};

const PRIMARY_NAV: NavItem[] = [
  { key: "calendar", to: appNav.calendar(), label: "Calendar", Icon: CalendarDays, end: true },
  { key: "events", to: appNav.events(), label: "Events", Icon: Ticket },
  { key: "finance", to: appNav.finance(), label: "Finance", Icon: Wallet },
];

export const WorkspaceLayout: React.FC = () => {
  const { user } = useAuth();
  const primaryNav = React.useMemo(() => {
    if (isOwner(user)) return PRIMARY_NAV;
    if (isOperator(user)) return PRIMARY_NAV.filter((item) => item.key === "events");
    if (isPromoter(user)) return PRIMARY_NAV.filter((item) => item.key === "calendar" || item.key === "events" || item.key === "finance");
    return PRIMARY_NAV;
  }, [user]);
  const directoryLinks = React.useMemo(() => {
    if (isOwner(user)) return DIRECTORY_LINKS;
    if (isPromoter(user)) return DIRECTORY_LINKS.filter((link) => link.key === "contacts");
    return [];
  }, [user]);
  const showGuestListLink = isOwner(user) || isPromoter(user);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(directoryActive(pathname));
  const [railDirectoryOpen, setRailDirectoryOpen] = useState(false);
  const [railPopoverPos, setRailPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const railDirectoryRef = useRef<HTMLDivElement | null>(null);
  const railPopoverRef = useRef<HTMLDivElement | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarFlyoutOpen, setSidebarFlyoutOpen] = useState(false);
  const openImport = useCallback(() => {
    navigate(`${appNav.events()}?import=1`);
  }, [navigate]);
  const paletteActions = useCommandPaletteActions(openImport);
  useCommandPaletteHotkey(() => setPaletteOpen(true));
  useScrollToTopOnNavigate();

  const isEventWorkspaceRoute =
    /^\/events\/[^/]+(\/.+)?$/.test(pathname) &&
    pathname !== "/events/new" &&
    !pathname.endsWith("/edit");

  const fitViewport =
    pathname === "/calendar" ||
    pathname === "/directory/guests" ||
    pathname === "/events" ||
    pathname === "/events/new" ||
    isEventWorkspaceRoute ||
    pathname === "/profile" ||
    pathname === "/finance" ||
    pathname.startsWith("/finance/");

  useEffect(() => {
    setMobileNavOpen(false);
    setSidebarFlyoutOpen(false);
    setRailDirectoryOpen(false);
    if (directoryActive(pathname)) setDirectoryOpen(true);
  }, [pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!railDirectoryOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = railDirectoryRef.current?.contains(target);
      const insidePopover = railPopoverRef.current?.contains(target);
      if (!insideTrigger && !insidePopover) setRailDirectoryOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setRailDirectoryOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [railDirectoryOpen]);

  const directoryIsActive = directoryActive(pathname);
  const tier = useDeviceTier();
  const isMobile = tier === "mobile";
  const mobileHeader = mobileHeaderFor(pathname);

  return (
    <div
      className={`app-shell-root app-shell-root--sidebar zoniq-premium-page${fitViewport ? " app-shell-root--fit" : ""}`}
    >
      <aside className="app-shell-sidebar app-shell-sidebar--collapsed" aria-label="Primary navigation">
        <div className="app-shell-sidebar-brand-row">
          <NavLink to={appNav.calendar()} className="app-shell-sidebar-brand" title="Zoniq EventOS" aria-label="Zoniq EventOS">
            <span className="app-shell-mark" aria-hidden>
              <img src={brandLogo} alt="" className="app-shell-mark-img" />
            </span>
          </NavLink>
        </div>

        <button
          type="button"
          className="app-shell-sidebar-collapse"
          onClick={() => setSidebarFlyoutOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={sidebarFlyoutOpen}
          title="Menu"
        >
          <PanelLeft className="app-shell-sidebar-collapse-icon" size={21} strokeWidth={2.4} />
        </button>

        <nav className="app-shell-sidebar-nav">
          {primaryNav.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              className={sidebarLinkClass}
              title={item.label}
              aria-label={item.label}
            >
              <item.Icon className="app-shell-sidebar-icon" size={24} strokeWidth={2} aria-hidden />
              <span className="app-shell-sidebar-label">{item.label}</span>
            </NavLink>
          ))}

          {showGuestListLink ? (
            <NavLink
              to={GUEST_LIST_LINK.to}
              className={sidebarLinkClass}
              title={GUEST_LIST_LINK.label}
              aria-label={GUEST_LIST_LINK.label}
            >
              <GUEST_LIST_LINK.Icon className="app-shell-sidebar-icon" size={24} strokeWidth={2} aria-hidden />
              <span className="app-shell-sidebar-label">{GUEST_LIST_LINK.label}</span>
            </NavLink>
          ) : null}

          {user?.is_system_admin ? (
            <NavLink
              to={appNav.ticketingLinks()}
              className={sidebarLinkClass}
              title="zoniq.in links"
              aria-label="zoniq.in links"
            >
              <Link2 className="app-shell-sidebar-icon" size={24} strokeWidth={2} aria-hidden />
              <span className="app-shell-sidebar-label">zoniq.in links</span>
            </NavLink>
          ) : null}

          <div
            className={`app-shell-sidebar-directory${directoryIsActive ? " is-active" : ""}`}
            ref={railDirectoryRef}
          >
            <button
              type="button"
              className={`app-shell-sidebar-directory-trigger${directoryIsActive ? " active" : ""}`}
              aria-label="Directory"
              aria-haspopup="menu"
              aria-expanded={railDirectoryOpen}
              title="Directory"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setRailPopoverPos({ top: rect.top, left: rect.right + 10 });
                setRailDirectoryOpen((open) => !open);
              }}
            >
              <FolderOpen className="app-shell-sidebar-icon" size={24} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </nav>

        <div className="app-shell-sidebar-footer">
          <Link
            to={appNav.newEvent()}
            className="app-shell-sidebar-cta app-shell-sidebar-cta--ghost"
            title="Add Event"
            aria-label="Add Event"
          >
            <Plus className="app-shell-sidebar-icon" size={24} strokeWidth={2.4} aria-hidden />
            <span className="app-shell-sidebar-label">Add Event</span>
          </Link>
          {user ? (
            <div className="app-shell-sidebar-profile">
              <NavLink to={appNav.profile()} className="app-shell-sidebar-profile-link" title="Profile" aria-label="Profile">
                <span className="app-shell-sidebar-profile-avatar" aria-hidden>
                  {avatarInitials(user)}
                </span>
                <span className="app-shell-sidebar-label app-shell-sidebar-profile-name">{user.full_name}</span>
              </NavLink>
            </div>
          ) : null}
        </div>
      </aside>

      {railPopoverPos
        ? createPortal(
            <div
              ref={railPopoverRef}
              className={`app-shell-sidebar-rail-popover${railDirectoryOpen ? " is-open" : ""}`}
              role="menu"
              style={{ top: railPopoverPos.top, left: railPopoverPos.left }}
            >
              <div className="app-shell-sidebar-rail-popover-title">Directory</div>
              {directoryLinks.map((link) => (
                <NavLink
                  key={link.key}
                  to={link.to}
                  role="menuitem"
                  className={({ isActive }) =>
                    `app-shell-sidebar-rail-popover-link${isActive ? " active" : ""}`
                  }
                  onClick={() => setRailDirectoryOpen(false)}
                  tabIndex={railDirectoryOpen ? undefined : -1}
                >
                  <link.Icon size={16} strokeWidth={2} aria-hidden />
                  <span>{link.label}</span>
                </NavLink>
              ))}
            </div>,
            document.body
          )
        : null}

      <Drawer.Root direction="left" open={sidebarFlyoutOpen} onOpenChange={setSidebarFlyoutOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="app-shell-sidebar-flyout-overlay" />
          <Drawer.Content className="app-shell-sidebar-flyout" aria-describedby={undefined}>
            <Drawer.Title className="visually-hidden">Navigation</Drawer.Title>
            <div className="app-shell-sidebar-flyout-head">
              <NavLink
                to={appNav.calendar()}
                className="app-shell-sidebar-brand"
                title="Zoniq EventOS"
                onClick={() => setSidebarFlyoutOpen(false)}
              >
                <span className="app-shell-mark" aria-hidden>
                  <img src={brandLogo} alt="" className="app-shell-mark-img" />
                </span>
                <span className="app-shell-sidebar-brand-text">
                  Zoniq EventOS
                  <span className="app-shell-brand-sub">Event operations</span>
                </span>
              </NavLink>
              <Drawer.Close asChild>
                <button type="button" className="app-shell-sidebar-flyout-close" aria-label="Close navigation menu">
                  <X size={18} strokeWidth={2.4} aria-hidden />
                </button>
              </Drawer.Close>
            </div>

            <nav className="app-shell-sidebar-nav app-shell-sidebar-flyout-nav">
              {primaryNav.map((item) => (
                <NavLink
                  key={`fly-${item.key}`}
                  to={item.to}
                  end={item.end}
                  className={sidebarLinkClass}
                  onClick={() => setSidebarFlyoutOpen(false)}
                >
                  <item.Icon className="app-shell-sidebar-icon" size={21} strokeWidth={2} aria-hidden />
                  <span className="app-shell-sidebar-label">{item.label}</span>
                </NavLink>
              ))}

              {showGuestListLink ? (
                <NavLink
                  to={GUEST_LIST_LINK.to}
                  className={sidebarLinkClass}
                  onClick={() => setSidebarFlyoutOpen(false)}
                >
                  <GUEST_LIST_LINK.Icon className="app-shell-sidebar-icon" size={21} strokeWidth={2} aria-hidden />
                  <span className="app-shell-sidebar-label">{GUEST_LIST_LINK.label}</span>
                </NavLink>
              ) : null}

              {user?.is_system_admin ? (
                <NavLink
                  to={appNav.ticketingLinks()}
                  className={sidebarLinkClass}
                  onClick={() => setSidebarFlyoutOpen(false)}
                >
                  <Link2 className="app-shell-sidebar-icon" size={21} strokeWidth={2} aria-hidden />
                  <span className="app-shell-sidebar-label">zoniq.in links</span>
                </NavLink>
              ) : null}

              <div className={`app-shell-sidebar-directory${directoryIsActive ? " is-active" : ""}`}>
                <button
                  type="button"
                  className={`app-shell-sidebar-directory-trigger${directoryIsActive ? " active" : ""}`}
                  aria-expanded={directoryOpen}
                  onClick={() => setDirectoryOpen((open) => !open)}
                >
                  <FolderOpen className="app-shell-sidebar-icon" size={21} strokeWidth={2} aria-hidden />
                  <span className="app-shell-sidebar-label">Directory</span>
                  <span className="app-shell-sidebar-directory-caret" aria-hidden>
                    {directoryOpen ? "▴" : "▾"}
                  </span>
                </button>
                <div className={`app-shell-sidebar-directory-group${directoryOpen ? " is-open" : ""}`}>
                  {directoryLinks.map((link) => (
                    <NavLink
                      key={`fly-${link.key}`}
                      to={link.to}
                      className={sidebarLinkClass}
                      onClick={() => setSidebarFlyoutOpen(false)}
                      tabIndex={directoryOpen ? undefined : -1}
                    >
                      <link.Icon className="app-shell-sidebar-icon" size={18} strokeWidth={2} aria-hidden />
                      <span className="app-shell-sidebar-label">{link.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            </nav>

            <div className="app-shell-sidebar-footer">
              <Link
                to={appNav.newEvent()}
                className="app-shell-sidebar-cta app-shell-sidebar-cta--ghost"
                onClick={() => setSidebarFlyoutOpen(false)}
              >
                <Plus className="app-shell-sidebar-icon" size={21} strokeWidth={2.4} aria-hidden />
                <span className="app-shell-sidebar-label">Add Event</span>
              </Link>
              {user ? (
                <div className="app-shell-sidebar-profile">
                  <NavLink
                    to={appNav.profile()}
                    className="app-shell-sidebar-profile-link"
                    onClick={() => setSidebarFlyoutOpen(false)}
                  >
                    <span className="app-shell-sidebar-profile-avatar" aria-hidden>
                      {avatarInitials(user)}
                    </span>
                    <span className="app-shell-sidebar-label app-shell-sidebar-profile-name">{user.full_name}</span>
                  </NavLink>
                </div>
              ) : null}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      <div className={`app-shell-body${mobileNavOpen ? " app-shell-body--drawer-open" : ""}`}>
        <header className={`app-shell-topbar${isMobile ? " app-shell-topbar--mobile" : ""}`}>
          {isMobile ? (
            /* The tab bar carries navigation now, so the header identifies the
               screen and offers the way back instead of a menu button. */
            <>
              {mobileHeader.backTo ? (
                <Link
                  to={mobileHeader.backTo}
                  className="app-shell-topbar-back"
                  aria-label={`Back to ${mobileHeader.backTo === appNav.events() ? "Events" : "previous screen"}`}
                >
                  <span aria-hidden>←</span>
                </Link>
              ) : null}
              <h1 className="app-shell-topbar-title">{mobileHeader.title}</h1>
            </>
          ) : (
            <>
              <button
                type="button"
                className="app-shell-menu-btn"
                aria-label={mobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={mobileNavOpen}
                aria-controls="app-shell-mobile-drawer"
                onClick={() => setMobileNavOpen((open) => !open)}
              >
                <span className="app-shell-menu-btn__bar" aria-hidden />
                <span className="app-shell-menu-btn__bar" aria-hidden />
                <span className="app-shell-menu-btn__bar" aria-hidden />
              </button>
              <NavLink to={appNav.calendar()} className="app-shell-topbar-brand">
                Zoniq EventOS
              </NavLink>
            </>
          )}
          <div className="app-shell-topbar-actions">
            <GlobalSearch compact />
          </div>
        </header>

        {directoryIsActive ? (
          <nav className="app-shell-directory-subnav" aria-label="Directory sections">
            <div className="app-shell-directory-subnav-inner">
              {directoryLinks.map((link, index) => (
                <React.Fragment key={link.key}>
                  {index > 0 ? <span className="app-shell-directory-subnav-sep" aria-hidden>·</span> : null}
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `app-shell-directory-subnav-link${isActive ? " active" : ""}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </React.Fragment>
              ))}
            </div>
          </nav>
        ) : null}

        <div
          className={`app-shell-mobile-overlay${mobileNavOpen ? " is-open" : ""}`}
          role="presentation"
          aria-hidden={!mobileNavOpen}
          onClick={() => setMobileNavOpen(false)}
        />

        <nav
          id="app-shell-mobile-drawer"
          className={`app-shell-mobile-drawer${mobileNavOpen ? " is-open" : ""}`}
          aria-label="Mobile navigation"
          aria-hidden={!mobileNavOpen}
        >
          <div className="app-shell-mobile-drawer-head">
            <span className="app-shell-mobile-drawer-title">Zoniq EventOS</span>
            <button
              type="button"
              className="app-shell-mobile-drawer-close"
              onClick={() => setMobileNavOpen(false)}
              aria-label="Close navigation menu"
            >
              ×
            </button>
          </div>
          <div className="app-shell-mobile-drawer-links">
            {primaryNav.map((item) => (
              <NavLink
                key={`mobile-${item.key}`}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `app-shell-mobile-link${isActive ? " active" : ""}`}
                onClick={() => setMobileNavOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
            {showGuestListLink ? (
              <NavLink
                to={GUEST_LIST_LINK.to}
                className={({ isActive }) => `app-shell-mobile-link${isActive ? " active" : ""}`}
                onClick={() => setMobileNavOpen(false)}
              >
                {GUEST_LIST_LINK.label}
              </NavLink>
            ) : null}
            {user?.is_system_admin ? (
              <NavLink
                to={appNav.ticketingLinks()}
                className={({ isActive }) => `app-shell-mobile-link${isActive ? " active" : ""}`}
                onClick={() => setMobileNavOpen(false)}
              >
                zoniq.in links
              </NavLink>
            ) : null}
            <NavLink
              to={appNav.artists()}
              className={`app-shell-mobile-link ${directoryIsActive ? "active" : ""}`}
              onClick={() => setMobileNavOpen(false)}
            >
              Directory
            </NavLink>
            <div className="app-shell-mobile-directory-group">
              {directoryLinks.map((link) => (
                <NavLink
                  key={`mobile-${link.key}`}
                  to={link.to}
                  className={({ isActive }) =>
                    `app-shell-mobile-link app-shell-mobile-directory-link${isActive ? " active" : ""}`
                  }
                  onClick={() => setMobileNavOpen(false)}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
          <div className="app-shell-mobile-drawer-footer">
            {user ? (
              <NavLink
                to={appNav.profile()}
                className="app-shell-mobile-link app-shell-mobile-profile-link"
                onClick={() => setMobileNavOpen(false)}
              >
                <span className="app-shell-sidebar-profile-avatar" aria-hidden>
                  {avatarInitials(user)}
                </span>
                {user.full_name}
              </NavLink>
            ) : null}
          </div>
        </nav>

        <main className={`app-shell-main app-shell-main--wide app-shell-main--centered${fitViewport ? " app-shell-main--fit" : ""}${pathname === "/profile" ? " app-shell-main--fit-flush" : ""}${isMobile ? " app-shell-main--tabbar" : ""}`}>
          <div key={pathname} className={`app-shell-outlet${fitViewport ? " app-shell-outlet--fit" : ""}`}>
            <Outlet context={{ workspace: APP_WORKSPACE }} />
          </div>
        </main>
      </div>
      {isMobile ? <MobileTabBar /> : null}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} actions={paletteActions} />
    </div>
  );
};
